import { Link, Outlet, useLocation, useNavigate, useParams, useRouter } from "@tanstack/react-router";
import { Compass, ListChecks } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Loading, Mark, Wordmark } from "../brand";
import { browsable, SERVER_HOST, useConnection } from "../connection";
import { here, signInFrom } from "./signin-link";
import { setAccountContext, useAccountContext, useOwners } from "./account-context";

const SignInView = lazy(() => import("../views/SignIn").then((m) => ({ default: m.SignInView })));
import { isSingle, useCapabilities, useInbox, useRepositories, useViewer } from "../shell-data";
import { RepoProvider } from "../repo";
import { Author, ConfirmHost, Icon, Toaster } from "../ui";
import { Role } from "../gen/gd_platform_pb";
import type { MenuMode } from "./CommandMenu";
import { endChord, modKey, startChord, typing, useTheme } from "./hooks";

// The command menu is its own chunk, fetched when idle or on first open.
const CommandMenu = lazy(() => import("./CommandMenu").then((m) => ({ default: m.CommandMenu })));
const AccountMenu = lazy(() => import("./AccountMenu").then((m) => ({ default: m.AccountMenu })));
const Shortcuts = lazy(() => import("./AccountMenu").then((m) => ({ default: m.Shortcuts })));

export function AppShell() {
  // Plain links into the app (in rendered markdown, notes and the like) move within it, as
  // the router's own links do, instead of loading the page again.
  const router = useRouter();
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.("a");
      const href = a?.getAttribute("href");
      if (!a || !href || a.target || a.hasAttribute("download") || !href.startsWith("/") || href.startsWith("//")) return;
      if (/^\/(login|assets)\//.test(href)) return;
      e.preventDefault();
      router.history.push(href);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [router]);
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { owner?: string; repo?: string; number?: string };
  const [theme, setTheme] = useTheme();
  const [menu, setMenu] = useState<MenuMode | null>(null);
  const [keys, setKeys] = useState(false);
  const [drawer, setDrawer] = useState(false);
  // What was typed before the menu could take focus; handed to it.
  const [early, setEarly] = useState("");
  const menuOpen = useRef(false);
  menuOpen.current = menu !== null || keys;
  const location = useLocation();
  const { signedIn } = useConnection();
  useEffect(() => {
    setDrawer(false);
  }, [location.pathname]);
  // The phone drawer closes with Escape, like every other overlay.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setDrawer(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawer]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setMenu((m) => (m ? null : "all"));
        return;
      }
      // While a menu is open every key belongs to it. Printable keys that arrive before its input
      // has focus are collected and handed to it, so nothing typed is lost.
      if (menuOpen.current && !typing(e) && e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setEarly((q) => q + e.key);
        return;
      }
      if (menuOpen.current || typing(e) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (endChord()) {
        // Views skip a key the shell took, so "g h" never also toggles History.
        e.preventDefault();
        const repo = params.owner && params.repo ? { owner: params.owner, repo: params.repo } : null;
        if (e.key === "h") navigate({ to: "/" });
        else if (e.key === "n" || (!repo && e.key === "i")) navigate({ to: "/inbox" });
        else if (repo && e.key === "i") navigate({ to: "/$owner/$repo/issues", params: repo });
        else if (repo && e.key === "a") navigate({ to: "/$owner/$repo/agents", params: repo });
        else if (repo && e.key === "s") navigate({ to: "/$owner/$repo/settings", params: repo });
        else if (repo && e.key === "c") navigate({ to: "/$owner/$repo/code/$", params: { ...repo, _splat: "" } });
        else if (repo && e.key === "l") navigate({ to: "/$owner/$repo/changes", params: repo });
        else if (repo && e.key === "r") navigate({ to: "/$owner/$repo/reviews", params: repo });
        else if (repo && e.key === "k") navigate({ to: "/$owner/$repo/locks", params: repo });
        else if (repo && e.key === "/") navigate({ to: "/$owner/$repo/search", params: repo });
        return;
      }
      if (e.key === "g") startChord();
      else if (e.key === "/") (e.preventDefault(), setMenu("all"));
      else if (e.key === "t" && params.repo) (e.preventDefault(), setMenu("files"));
      else if (e.key === "?") setKeys(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, params.owner, params.repo]);

  const [menuLoaded, setMenuLoaded] = useState(false);
  useEffect(() => {
    if (menu) setMenuLoaded(true);
  }, [menu]);
  useEffect(() => {
    // Warm the command menu once the first screen is up, so it never competes with it.
    const id = setTimeout(() => void import("./CommandMenu"), 3000);
    return () => clearTimeout(id);
  }, []);
  useEffect(() => {
    if (!menu) setEarly("");
  }, [menu]);
  const commandMenu = menuLoaded || menu ? (
    <Suspense
      fallback={
        menu && (
          <div className="overlay">
            <div className="palette">
              <input className="palette-early" autoFocus value={early} onChange={(e) => setEarly(e.target.value)} placeholder="Search files, repositories, change requests and actions…" aria-label="Command menu" />
            </div>
          </div>
        )
      }
    >
      <CommandMenu open={menu} initialQuery={early} onClose={() => setMenu(null)} theme={theme} setTheme={setTheme} showShortcuts={() => setKeys(true)} />
    </Suspense>
  ) : null;

  // Signed out, only organisations and repositories can be browsed.
  if (!signedIn && !browsable(location.pathname))
    return (
      <Suspense fallback={<Loading />}>
        <SignInView />
      </Suspense>
    );
  return (
    <div className="app" data-drawer={drawer || undefined}>
      <button type="button" className="skip" onClick={() => document.getElementById("main")?.focus()}>
        Skip to content
      </button>
      <Sidebar onSearch={() => setMenu("all")} theme={theme} setTheme={setTheme} showShortcuts={() => setKeys(true)} />
      {drawer && <div className="scrim" onClick={() => setDrawer(false)} aria-hidden="true" />}
      <div className="main">
        <TopBar onMenu={() => setDrawer(true)} />
        <main className="content" id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
      {params.owner && params.repo ? (
        <RepoProvider owner={params.owner} name={params.repo}>
          {commandMenu}
        </RepoProvider>
      ) : (
        commandMenu
      )}
      <Toaster />
      <ConfirmHost />
      {keys && (
        <Suspense>
          <Shortcuts open onClose={() => setKeys(false)} />
        </Suspense>
      )}
    </div>
  );
}

function Sidebar({ onSearch, theme, setTheme, showShortcuts }: { onSearch(): void; theme: string; setTheme(t: "system" | "light" | "dark"): void; showShortcuts(): void }) {
  const { signedIn } = useConnection();
  const viewer = useViewer();
  const repos = useRepositories();
  const inbox = useInbox();
  const reviews = useCapabilities().data?.reviews ?? false;
  const mine = useOwners();
  // Your own account counts the repositories under your name.
  const owners = mine.map((o) => (o.personal ? { ...o, repositories: repos.data?.filter((r) => r.owner === o.login).length ?? 0 } : o));
  const current = useAccountContext(owners, repos.data?.map((r) => r.owner));
  // Opening something of one of the viewer's accounts (a repository, an organisation, their
  // page) makes it the account the sidebar shows.
  const { pathname } = useLocation();
  const first = pathname.split("/")[1];
  useEffect(() => {
    if (owners.some((o) => o.login === first)) setAccountContext(first!);
  }, [first, owners]);
  // The sidebar lists the current account's repositories; on a server of one repository, that one.
  const listed = current ? repos.data?.filter((r) => r.owner === current.login) : repos.data;
  const manyOwners = !current && new Set(repos.data?.map((r) => r.owner)).size > 1;
  const switcher = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  // The menu opens under the card (a popover sits in the top layer, placed here).
  useEffect(() => {
    const menu = switcher.current;
    const place = () => {
      const r = opener.current?.getBoundingClientRect();
      if (r && menu) Object.assign(menu.style, { top: `${r.bottom + 4}px`, left: `${r.left}px`, width: `${Math.max(r.width, 260)}px` });
    };
    menu?.addEventListener("beforetoggle", place);
    return () => menu?.removeEventListener("beforetoggle", place);
  });
  const waiting = inbox.data?.length ?? 0;
  const unread = inbox.data?.filter((i) => i.unread).length ?? 0;
  const now = Date.now();
  const me = (
    <>
      {viewer.data?.principal ? <Author author={viewer.data.principal} compact /> : <Icon name="users" />}
      <span style={{ minWidth: 0 }}>
        <span className="who-name">{viewer.data?.account?.displayName ?? (viewer.data?.principal || "Anonymous")}</span>
        <span className="conn">{viewer.data?.principal ? `@${viewer.data.principal}` : SERVER_HOST}</span>
      </span>
    </>
  );
  // Swapping the button would drop keyboard focus, so a focused one waits for its click.
  const [account, setAccount] = useState<"later" | "now" | "open">("later");
  const plain = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const id = setTimeout(() => document.activeElement !== plain.current && setAccount((a) => (a === "later" ? "now" : a)), 3000);
    return () => clearTimeout(id);
  }, []);
  const plainMe = (
    <button ref={plain} type="button" className="me" aria-label="Account and settings" aria-haspopup="menu" onClick={() => setAccount("open")}>
      {me}
    </button>
  );

  if (!signedIn) {
    return (
      <nav className="sidebar" aria-label="Main">
        <div className="workspace">
          <a href="https://grovedock.com" className="lockup" aria-label="Grovedock home">
            <Mark size={22} />
            <Wordmark height={15} />
          </a>
        </div>
        <div className="side-scroll visitor">
          <Link to="/explore" className="side-link">
            <Icon name={Compass} />
            <span className="label">Explore</span>
          </Link>
          <p className="muted">You're browsing as a visitor. Sign in to star, review, and land changes.</p>
          <button type="button" className="button" data-primary="" onClick={() => signInFrom(here())}>
            Sign in
          </button>
          <a className="button button-quiet" href="https://grovedock.com/pricing.html">
            Plans and pricing
          </a>
        </div>
      </nav>
    );
  }
  return (
    <nav className="sidebar" aria-label="Main">
      <div className="workspace">
        <Link to="/" className="lockup" aria-label="Grovedock home">
          <Mark size={22} />
          <Wordmark height={15} />
        </Link>
      </div>
      {current && (
        <>
          <button ref={opener} type="button" className="org-switch" popoverTarget="account-switch" aria-label={`Account: ${current.name}. Switch account`}>
            <span className="org-avatar" data-personal={current.personal || undefined} aria-hidden="true">
              {current.name.slice(0, 1).toUpperCase()}
            </span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span className="name">{current.name}</span>
              <span className="sub">{current.personal ? `Your account, ${current.repositories} ${current.repositories === 1 ? "repository" : "repositories"}` : `${current.repositories} ${current.repositories === 1 ? "repository" : "repositories"}, ${current.agents} ${current.agents === 1 ? "agent" : "agents"}`}</span>
            </span>
            <Icon name="down" size={14} />
          </button>
          <div ref={switcher} id="account-switch" popover="auto" className="switch-menu">
            <ul aria-label="Switch account">
              {owners.map((o) => (
                <li key={o.login}>
                  <Link
                    to="/$owner"
                    params={{ owner: o.login }}
                    aria-current={o.login === current.login ? "true" : undefined}
                    onClick={() => (setAccountContext(o.login), switcher.current?.hidePopover())}
                  >
                    <span className="org-avatar" data-personal={o.personal || undefined} aria-hidden="true">
                      {o.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span className="name">{o.name}</span>
                      <span className="sub">{o.personal ? `@${o.login}, your account` : o.role >= Role.ADMIN ? "Organisation, you administer it" : "Organisation"}</span>
                    </span>
                    {o.login === current.login && <Icon name="check" size={14} />}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="switch-foot">
              <Link to="/new" search={{ owner: current.login }} onClick={() => switcher.current?.hidePopover()}>
                <Icon name="plus" size={14} /> New repository
              </Link>
              <Link to="/import" search={{ owner: current.login }} onClick={() => switcher.current?.hidePopover()}>
                <Icon name="download" size={14} /> Bring one from GitHub
              </Link>
              {viewer.data?.platform && (
                <Link to="/welcome" onClick={() => switcher.current?.hidePopover()}>
                  <Icon name="users" size={14} /> New organisation
                </Link>
              )}
            </div>
          </div>
        </>
      )}
      <button type="button" className="side-search" onClick={onSearch}>
        <Icon name="search" size={15} />
        <span>Search or jump to</span>
        <kbd>{modKey}K</kbd>
      </button>
      <div className="side-scroll">
        <Link to="/" className="side-link" activeOptions={{ exact: true }}>
          <Icon name="home" />
          <span className="label">Home</span>
        </Link>
        {reviews && (
        <Link to="/inbox" className="side-link">
          <Icon name="inbox" />
          <span className="label">Inbox</span>
          {waiting > 0 && (
            <span className="count" data-hot={unread > 0 || undefined} aria-label={`${waiting} waiting, ${unread} new`}>
              {waiting}
            </span>
          )}
        </Link>
        )}
        {reviews && (
          <Link to="/reviews" className="side-link">
            <Icon name={ListChecks} />
            <span className="label">Reviews</span>
          </Link>
        )}
        <Link to="/explore" className="side-link">
          <Icon name={Compass} />
          <span className="label">Explore</span>
        </Link>
        <div className="side-section">
          Repositories
          {current && (
            <Link to="/new" search={{ owner: current.login }} className="side-add" aria-label={`New repository in ${current.name}`} title="New repository">
              <Icon name="plus" size={14} />
            </Link>
          )}
        </div>
        {listed?.length === 0 && current && (
          <>
            <Link to="/new" search={{ owner: current.login }} className="side-link side-start">
              <span className="side-start-ring" aria-hidden="true" />
              <span className="label">New repository</span>
            </Link>
            <Link to="/import" search={{ owner: current.login }} className="side-link side-start">
              <span className="side-start-ring" aria-hidden="true" />
              <span className="label">Bring a repository</span>
            </Link>
          </>
        )}
        {listed?.map((r) => {
          const live = now - Number(r.tipAtMs) < 90_000;
          return (
            <Link key={r.name} to="/$owner/$repo" params={{ owner: r.owner, repo: r.name }} className="side-link" activeOptions={{ exact: false }}>
              <span className="pulse" data-live={live || undefined} aria-label={live ? "landing now" : undefined} />
              <span className="label">{isSingle(`${r.owner}/${r.name}`) ? SERVER_HOST : manyOwners ? `${r.owner}/${r.name}` : r.name}</span>
              {r.openChangeRequests > 0 && <span className="count" title={`${r.openChangeRequests} open change requests`}>{r.openChangeRequests}</span>}
            </Link>
          );
        })}
        {repos.isPending && !repos.isError && <div className="side-link muted">Loading…</div>}
        {repos.isError && <div className="side-link muted">Couldn't load</div>}
      </div>
      <div className="side-foot">
        {/* The menu (and the accessibility library under it) loads after the first screen, or on first use. */}
        {account === "later" ? (
          plainMe
        ) : (
          <Suspense fallback={plainMe}>
            <AccountMenu showShortcuts={showShortcuts} open={account === "open"}>
              {me}
            </AccountMenu>
          </Suspense>
        )}
        <button
          type="button"
          className="icon-button"
          onClick={() => setTheme(theme === "dark" ? "light" : theme === "light" ? "system" : "dark")}
          aria-label={`Theme: ${theme}. Change theme`}
          title={`Theme: ${theme}`}
        >
          <Icon name={theme === "dark" ? "moon" : theme === "light" ? "sun" : "monitor"} />
        </button>
      </div>
    </nav>
  );
}

const sections: Record<string, string> = {
  code: "Code",
  changes: "History",
  reviews: "Change requests",
  issues: "Issues",
  locks: "Locks",
  agents: "Agents",
  settings: "Settings",
};

function TopBar({ onMenu }: { onMenu(): void }) {
  const params = useParams({ strict: false }) as { owner?: string; repo?: string; number?: string };
  const { pathname } = useLocation();
  const label = SERVER_HOST;
  const section = params.repo ? pathname.split("/")[3] : undefined;
  const single = !!params.owner && !!params.repo && isSingle(`${params.owner}/${params.repo}`);
  const title = pathname === "/" ? "Home" : pathname === "/inbox" ? "Inbox" : pathname === "/reviews" ? "Reviews" : pathname === "/status" ? "Status" : pathname === "/explore" ? "Explore" : pathname === "/welcome" ? "Welcome" : pathname === "/settings/notifications" ? "Notifications" : pathname.startsWith("/settings") ? "Tokens" : null;
  // Each tab says where it is: "#12 · Issues · acme-labs/web · Grovedock".
  useEffect(() => {
    const where = params.repo ? (single ? label : `${params.owner}/${params.repo}`) : (params.owner ?? title);
    const parts = [params.number && `#${params.number}`, section && sections[section], where, "Grovedock"];
    document.title = parts.filter(Boolean).join(" · ");
  }, [params.owner, params.repo, params.number, section, single, label, title]);

  return (
    <header className="topbar">
      <button type="button" className="icon-button mobile-nav" onClick={onMenu} aria-label="Open navigation">
        <Icon name="menu" />
      </button>
      <nav className="crumbs-top" aria-label="Breadcrumbs">
        {title && <span aria-current="page">{title}</span>}
        {params.owner && !single && (
          <Link to="/$owner" params={{ owner: params.owner }}>
            {params.owner}
          </Link>
        )}
        {params.repo && (
          <>
            {!single && <span className="sep">/</span>}
            <Link to="/$owner/$repo" params={{ owner: params.owner!, repo: params.repo }} activeOptions={{ exact: true }}>
              {single ? label : params.repo}
            </Link>
          </>
        )}
        {section && sections[section] && (
          <>
            <span className="sep">/</span>
            {params.number ? (
              <Link to={section === "issues" ? "/$owner/$repo/issues" : "/$owner/$repo/reviews"} params={{ owner: params.owner!, repo: params.repo! }}>
                {sections[section]}
              </Link>
            ) : (
              <span aria-current="page">{sections[section]}</span>
            )}
          </>
        )}
        {params.number && (
          <>
            <span className="sep">/</span>
            <span aria-current="page">#{params.number}</span>
          </>
        )}
      </nav>
      <span className="spacer" />
    </header>
  );
}
